import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MasterConfirmDialog, associationIcon } from '@/features/masters';
import { DeleteBlockedNotice } from '@/shared/components/DeleteBlockedNotice';
import { blockedLead } from '@/shared/services/api/associationConflict';
import type { AssociatedRecord } from '@/shared/services/api/associationConflict';
import type { TemplateDefinition } from '../../overview/models';
import type { TemplateRecord } from '../../overview/models/template.types';
import { TemplateModal } from '../../overview/components/TemplateModal';

/**
 * The three confirmations, all on the masters' own popup (2026-10-09).
 *
 * `MasterConfirmDialog` -- the tinted icon tile, the bold question, one sentence, the footer buttons -- is the popup
 * every master and the builder's field delete already use, so the templates' Delete, Deactivate and Saved read and
 * look as the rest of the platform's do: danger for a delete, warning for a deactivation, success with a single Done
 * for a save.
 */

interface StatusConfirmProps {
  row: TemplateRecord | null;
  /** For the copy: who uses what this kind produces, and what they do with it. */
  definition: TemplateDefinition;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Activate / Deactivate, with the reference's own copy.
 *
 * The heading and the body both name the template, which is what makes the
 * confirmation worth having — and, incidentally, why a template's name is unique
 * within its kind.
 */
export function TemplateStatusConfirm({
  row,
  definition,
  busy,
  onCancel,
  onConfirm,
}: StatusConfirmProps) {
  if (!row) return null;

  const isActive = row.statusName === 'Active';
  const action = isActive ? 'Deactivate' : 'Activate';
  /*
    The kind's own sentence. The reference addresses Initiators on every kind,
    which is wrong on Supplier Onboarding -- suppliers are given that form, they
    do not select it.

    The template is named in the title above, so the body says "it" rather than
    repeating the name. That is what lets each sentence be a constant on the
    definition instead of a template string assembled here.
  */
  /* A kind may name the template in the sentence too, as the prototype does ("{name}", 2026-10-19). */
  const message = (isActive ? definition.deactivateConsequence : definition.activateConsequence).replace(
    '{name}',
    row.name,
  );

  return (
    <MasterConfirmDialog
      visible
      tone={isActive ? 'warning' : 'success'}
      icon={isActive ? 'pi-ban' : 'pi-check-circle'}
      title={`${action} "${row.name}"?`}
      message={message}
      confirmLabel={action}
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

interface DeleteConfirmProps {
  row: TemplateRecord | null;
  busy: boolean;
  /**
   * Why the platform refused, when it did: the records still holding this template. Given, the dialog becomes
   * the "Can't delete" card -- the same one every master shows -- instead of the question. Null until a delete
   * has actually been refused.
   */
  refusal?: { records: AssociatedRecord[]; message: string | null } | null;
  /** The kind's own sentence (`deleteMessage`), where it words it differently. */
  message?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Delete.
 *
 * The reference's copy says the template "will be permanently removed". This
 * platform does not physically delete master-like records: a delete writes its
 * own status, no listing returns the row, and the row survives so existing
 * references resolve. **The behaviour follows the platform and the wording
 * follows the behaviour** — the second sentence is the reference's own. The BID kind words it as the
 * reference does (2026-10-21, `message`): "This template will be permanently removed. …".
 *
 * Drawn by the masters' `MasterConfirmDialog` (2026-10-08) -- the confirmation every delete in the application
 * uses, the template builder's own field delete included, and the shape Niraxo_Supplier.html draws: a 520px popup
 * with rounded (14px) corners, the red trash tile, the question, the sentence, then Cancel and Delete.
 */
export function TemplateDeleteConfirm({
  row,
  busy,
  refusal = null,
  message = 'This template will be removed from the list. Records already created from it are unaffected.',
  onCancel,
  onConfirm,
}: DeleteConfirmProps) {
  return (
    <MasterConfirmDialog
      visible={row !== null}
      tone="danger"
      icon="pi-trash"
      title={row ? `Delete "${row.name}"?` : ''}
      message={message}
      confirmLabel={busy ? 'Deleting…' : 'Delete'}
      busy={busy}
      blocked={
        refusal
          ? {
              title: `Can't delete ${row?.name ?? 'this template'}`,
              lead: blockedLead(refusal.records, refusal.message),
              body: <DeleteBlockedNotice records={refusal.records} iconFor={associationIcon} />,
            }
          : undefined
      }
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

interface SavedDialogProps {
  open: boolean;
  /** True when the save was a draft, which changes both the heading and the message. */
  draft: boolean;
  /** True when an existing template was saved over (Update Template): "Template Updated". */
  updated?: boolean;
  name: string;
  /** What the kind calls an unnamed template. */
  fallbackName: string;
  /** The kind's own message, for a completed save. */
  message: string;
  /**
   * An Update Template (Supplier Onboarding's edit): the template's code. The same popup then reads "Template
   * Updated" and "Template <code> updated successfully." in place of the saved copy.
   */
  updatedCode?: string | null;
  /**
   * The template's code to name beside its name, on a create, a draft and an update alike (the PR Template,
   * 2026-10-14): "Template PRT-001 "Office Supplies" created successfully."
   */
  codeWithName?: string | null;
  /** The Template Code a Save as Draft was saved under, named in "Draft Saved" when known (2026-10-14). */
  draftCode?: string | null;
  /**
   * The prototype's popup (2026-10-19): a subtitle, the Template Code and Template Title, and what happens now --
   * drawn in place of the one-sentence popup where the kind supplies it (`savedDetail`).
   */
  detail?: TemplateDefinition['savedDetail'];
  /**
   * The BID layout's summary (2026-10-21): a completed save reads "Template Saved" / "Template Updated" with the
   * kind's line under it, then the Template Code and Template Title rows and a green note -- `message` after a
   * create, `updatedMessage` after an edit. A draft keeps the "Draft Saved" popup.
   */
  summary?: { code: string | null; subtitle: string; updatedMessage: string } | null;
  onDone: () => void;
}

/** The popup a successful save opens: the masters' popup in its success form, Done alone. */
export function TemplateSavedDialog({
  open,
  draft,
  updated = false,
  name,
  fallbackName,
  message,
  updatedCode = null,
  codeWithName = null,
  draftCode = null,
  detail,
  summary = null,
  onDone,
}: SavedDialogProps) {
  if (!open) return null;

  const shown = name.trim() || fallbackName;

  if (detail) {
    const isEdit = updated || (updatedCode !== null && updatedCode !== '');
    const code = draftCode ?? codeWithName ?? updatedCode ?? '—';
    return (
      <TemplateModal
        open
        /* design21 (2026-10-22): 480 wide, the code and title in one card. */
        width={480}
        layer="confirm"
        d16
        surfaceClassName="templates-overlay__surface--saved"
        tone="success"
        icon="pi-check-circle"
        title={isEdit ? 'Template Updated' : 'Template Saved'}
        subtitle={isEdit ? detail.editSubtitle : detail.createSubtitle}
        onClose={onDone}
        footer={
          <button type="button" className="templates__btn templates__btn--primary" onClick={onDone}>
            <i className="ti ti-check" aria-hidden="true" />
            Done
          </button>
        }
      >
        <div className="templates__saved">
          {/* One card, a row each: the value over its caption (design21, 2026-10-22). */}
          <div className="templates__saved-card">
            <div className="templates__saved-row">
              <i className="ti ti-id-badge-2 templates__saved-icon" aria-hidden="true" />
              <div className="templates__saved-text">
                <span className="templates__saved-value">{code}</span>
                <span className="templates__saved-label">Template Code</span>
              </div>
            </div>
            <div className="templates__saved-row">
              <i className="ti ti-file-text templates__saved-icon" aria-hidden="true" />
              <div className="templates__saved-text">
                <span className="templates__saved-value">{shown}</span>
                <span className="templates__saved-label">Template Title</span>
              </div>
            </div>
          </div>
          <p className="templates__saved-info">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{isEdit ? detail.editInfo : detail.createInfo}</span>
          </p>
        </div>
      </TemplateModal>
    );
  }
  /* An Update Template: by the flag, or by the code a kind names in its message (Supplier Onboarding). */
  const hasCode = updatedCode !== null && updatedCode !== '';
  const isUpdate = updated || hasCode;
  /* `PRT-001 "Office Supplies"` where the kind names its code, else `"Office Supplies"`. */
  const named = codeWithName ? `${codeWithName} "${shown}"` : `"${shown}"`;
  const draftNamedCode = draftCode ?? codeWithName;

  if (summary && !draft && !hasCode) {
    const title = isUpdate ? 'Template Updated' : 'Template Saved';
    /*
      The reference's own popup (ReviewAndSubmitPage_And_BIDOverview.html, 2026-10-22): the green check over the title
      and its line, the close control beside them, then one card holding the Template Code and Template Title -- each a
      blue glyph, the value in bold and its caption under it -- the green note, and Done with its check.
    */
    return (
      <Dialog
        maskClassName="ms-mask"
        visible
        modal
        draggable={false}
        resizable={false}
        className="ms-confirm templates__saved-dialog"
        showHeader
        header={null}
        aria-label={title}
        onHide={onDone}
        footer={
          <div className="flex justify-content-end">
            <Button type="button" label="Done" icon="pi pi-check" onClick={onDone} />
          </div>
        }
      >
        <div className="templates__saved-head">
          <span className="templates__saved-check" aria-hidden="true">
            <i className="pi pi-check-circle" />
          </span>
          <div className="templates__saved-heading">
            <p className="templates__saved-title">{title}</p>
            <p className="templates__saved-sub">
              {isUpdate ? 'Your changes to this template have been saved.' : summary.subtitle}
            </p>
          </div>
        </div>
        <div className="templates__saved">
          <div className="templates__saved-row">
            <i className="pi pi-id-card templates__saved-icon" aria-hidden="true" />
            <div className="templates__saved-text">
              <span className="templates__saved-value templates__saved-value--code">{summary.code ?? '—'}</span>
              <span className="templates__saved-label">Template Code</span>
            </div>
          </div>
          <div className="templates__saved-row">
            <i className="pi pi-file templates__saved-icon" aria-hidden="true" />
            <div className="templates__saved-text">
              <span className="templates__saved-value">{shown}</span>
              <span className="templates__saved-label">Template Title</span>
            </div>
          </div>
        </div>
        <p className="templates__saved-note">
          <i className="pi pi-info-circle" aria-hidden="true" />
          <span>{isUpdate ? summary.updatedMessage : message}</span>
        </p>
      </Dialog>
    );
  }

  return (
    <MasterConfirmDialog
      visible
      tone="success"
      icon="pi-check-circle"
      /* A code is only named for Update Template, never for Save as Draft, so it reads as an update first. */
      title={hasCode ? 'Template Updated' : draft ? 'Draft Saved' : isUpdate ? 'Template Updated' : 'Template Saved'}
      /* One wording for every outcome (QA, 2026-10-12): "Template <what happened> successfully." */
      message={
        hasCode
          ? `Template ${updatedCode ?? ''} updated successfully.`
          : draft
            ? /* Template **BIDT-003 – "BID Template demo"** has been saved as a draft successfully. ... (2026-10-14) */
              (
                <>
                  Template <strong>{`${draftNamedCode ? `${draftNamedCode} – ` : ''}"${shown}"`}</strong> has been
                  saved as a draft successfully. You can complete the configuration at any time.
                </>
              )
            : isUpdate
              ? `Template ${named} updated successfully.`
              : `Template ${named} created successfully. ${message}`
      }
      confirmLabel="Done"
      single
      onCancel={onDone}
      onConfirm={onDone}
    />
  );
}
